/**
 * Reusable cell renderer system for DataTable components
 *
 * This module provides a comprehensive set of cell renderer functions
 * that can be used across different table implementations. Each renderer
 * is type-safe, composable, and follows consistent patterns.
 *
 * @example
 * ```tsx
 * import { renderPrice, renderLink, renderDateTime } from './cellRenderers';
 *
 * const columns = [
 *   {
 *     key: 'price',
 *     label: 'Price',
 *     renderCell: (item) => renderPrice(item.amount)
 *   },
 *   {
 *     key: 'customer',
 *     label: 'Customer',
 *     renderCell: (item) => renderLink(item.customer.name, `/customers/${item.customer.id}`)
 *   }
 * ];
 * ```
 */

import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  formatDate,
  formatPrice,
  formatTime,
  formatInTimezone,
  getTimezoneAbbreviation,
  measureTextWidth,
} from './formatters';
import {
  renderStatusBadge,
  renderInvoicedBadge,
  renderActivityTypeBadge,
  renderDeviceBadges,
  renderFormatBadge,
  renderCustomerTypeBadge,
  renderTBDBadge,
  renderHostBadge,
} from './badgeRenderers';

// Export badge and formatter utilities for direct use
export * from './badgeRenderers';
export * from './formatters';
export * from './colorUtils';

/**
 * Type definition for a cell renderer function
 */
export type CellRenderer<T = any> = (value: T, options?: any) => React.ReactNode;

/**
 * Configuration for a cell renderer registry
 */
export interface CellRendererConfig {
  renderer: CellRenderer;
  tooltip?: (value: any) => string;
}

/**
 * Registry of cell renderers by type
 */
export interface CellRendererRegistry {
  [key: string]: CellRendererConfig;
}

// ============================================================================
// Basic Renderers
// ============================================================================

/**
 * Render a simple text value
 */
export const renderText = (value: string | undefined | null): React.ReactNode => {
  return value || null;
};

/**
 * Render a code-formatted value (monospace, colored)
 */
export const renderCode = (value: string | undefined | null, className?: string): React.ReactNode => {
  if (!value) return null;
  return <code className={className || 'text-primary'}>{value}</code>;
};

/**
 * Render a semibold text value
 */
export const renderSemibold = (value: string | undefined | null): React.ReactNode => {
  if (!value) return null;
  return <div className="fw-semibold">{value}</div>;
};

/**
 * Render a wrapped text value with optional styling
 */
export const renderWrappedText = (
  value: string | undefined | null,
  className?: string
): React.ReactNode => {
  if (!value) return null;
  return <div className={className || 'small text-wrap'}>{value}</div>;
};

// ============================================================================
// Link Renderers
// ============================================================================

/**
 * Render a link (internal navigation or external)
 */
export const renderLink = (
  text: string | undefined | null,
  url: string | undefined | null,
  external: boolean = false
): React.ReactNode => {
  if (!text || !url) return text || null;

  const props = external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <a href={url} className="text-decoration-none" {...props}>
      {text}
    </a>
  );
};

/**
 * Render an email link
 */
export const renderEmailLink = (email: string | undefined | null): React.ReactNode => {
  if (!email) return null;
  return (
    <a href={`mailto:${email}`} className="text-decoration-none">
      {email}
    </a>
  );
};

/**
 * Render a phone link
 */
export const renderPhoneLink = (phone: string | undefined | null): React.ReactNode => {
  if (!phone) return null;
  return (
    <a href={`tel:${phone}`} className="text-decoration-none">
      {phone}
    </a>
  );
};

/**
 * Render a QuickBooks invoice link
 */
export const renderQBOInvoiceLink = (
  invoiceNumber: string | undefined | null,
  invoiceSuffix: string | undefined | null,
  invoiceId: string | undefined | null,
  srcId: string | undefined | null
): React.ReactNode => {
  if (!invoiceId || !srcId) {
    return null;
  }

  const qboUrl = `https://qbo.intuit.com/login?deeplinkcompanyid=${srcId}&pagereq=app/invoice?txnId=${invoiceId}`;
  const invoiceDisplay = `${invoiceNumber || ''}${invoiceSuffix || ''}`;

  return (
    <a href={qboUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
      {invoiceDisplay}
    </a>
  );
};

// ============================================================================
// Price/Currency Renderers
// ============================================================================

/**
 * Render a price with currency formatting and styling
 */
export const renderPrice = (
  price: number | undefined | null,
  options?: {
    className?: string;
    align?: 'start' | 'center' | 'end';
  }
): React.ReactNode => {
  if (price === undefined || price === null) return null;

  const className = options?.className || 'fw-semibold text-success';
  const align = options?.align || 'end';

  return (
    <div className={`text-${align}`}>
      <span className={className}>{formatPrice(price)}</span>
    </div>
  );
};

// ============================================================================
// Date/Time Renderers
// ============================================================================

/**
 * Render a formatted date
 */
export const renderDate = (dateStr: string | undefined | null): React.ReactNode => {
  const formatted = formatDate(dateStr);
  return formatted || null;
};

/**
 * Render a formatted time
 */
export const renderTime = (timeStr: string | undefined | null): React.ReactNode => {
  const formatted = formatTime(timeStr);
  return formatted !== 'N/A' ? formatted : null;
};

/**
 * DateTime configuration for rendering
 */
export interface DateTimeConfig {
  startTime?: string;
  endTime?: string;
  timezone?: string;
  tbd?: boolean;
  hasDay?: boolean;
  hasTime?: boolean;
  endHasTime?: boolean;
}

/**
 * Render a date/time range with timezone support
 * Handles TBD dates/times and timezone conversions
 */
export const renderDateTime = (config: DateTimeConfig): React.ReactNode => {
  // Handle TBD flag
  if (config.tbd) {
    return renderTBDBadge();
  }

  if (!config.startTime) {
    return null;
  }

  // Parse UTC datetime from API
  const startDate = new Date(config.startTime);
  const endDate = config.endTime ? new Date(config.endTime) : null;

  // Get IANA timezone name and extract abbreviation
  const tzName = config.timezone || '';
  const tz = tzName && config.hasTime ? getTimezoneAbbreviation(tzName, startDate) : '';

  // Format date and time in the event's timezone
  const startFormatted = formatInTimezone(startDate, tzName || 'UTC');
  const hasTimezoneError = startFormatted.error;

  const dateStr = `${startFormatted.month}/${config.hasDay ? startFormatted.day : 'TBD'}/${startFormatted.year}`;

  const startTimeStr = config.hasTime
    ? `${startFormatted.hour}:${startFormatted.minute} ${startFormatted.dayPeriod}`
    : 'TBD';

  const endTimeStr =
    endDate && config.endTime && config.endHasTime
      ? (() => {
          const endFormatted = formatInTimezone(endDate, tzName || 'UTC');
          return `${endFormatted.hour}:${endFormatted.minute} ${endFormatted.dayPeriod}`;
        })()
      : 'TBD';

  // Render: {date} {startTime} - {endTime} {tz}
  // If bad timezone, render everything in red
  return (
    <div
      className="d-flex align-items-center gap-1"
      style={hasTimezoneError ? { color: 'red' } : {}}
    >
      <span>{dateStr}</span>
      <span>{startTimeStr}</span>
      <span>-</span>
      <span>{endTimeStr}</span>
      {tz && <span>{tz}</span>}
    </div>
  );
};

// ============================================================================
// Tooltip Renderers
// ============================================================================

/**
 * Configuration for shift tooltip
 */
export interface ShiftTooltipConfig {
  userName?: string;
  summary?: string;
  status?: string;
  type?: string;
  locationName?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  // Server-computed: this shift's period does not overlap the event's, so it
  // cannot be covering it. Only meaningful for a shift shown against an event.
  mismatch?: boolean;
}

/**
 * Render content with a tooltip overlay
 */
export const renderWithTooltip = (
  content: React.ReactNode,
  tooltipContent: React.ReactNode,
  tooltipId: string,
  options?: {
    placement?: 'top' | 'bottom' | 'left' | 'right';
    delay?: { show: number; hide: number };
    maxWidth?: number;
  }
): React.ReactNode => {
  const placement = options?.placement || 'top';
  const delay = options?.delay || { show: 250, hide: 400 };

  const tooltip = (
    <Tooltip id={tooltipId}>
      {options?.maxWidth && (
        <style>{`
          #${tooltipId} .tooltip-inner {
            max-width: ${options.maxWidth}px !important;
          }
        `}</style>
      )}
      {tooltipContent}
    </Tooltip>
  );

  const popperConfig = {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
        },
      },
    ],
  };

  return (
    <OverlayTrigger
      placement={placement}
      overlay={tooltip}
      trigger={['hover', 'focus']}
      delay={delay}
      popperConfig={popperConfig}
    >
      <span style={{ display: 'inline-block' }}>{content}</span>
    </OverlayTrigger>
  );
};

/**
 * Render a shift tooltip content
 */
export const renderShiftTooltipContent = (shift: ShiftTooltipConfig): React.ReactNode => {
  return (
    <div className="text-start">
      {shift.mismatch && (
        <div className="fw-semibold mb-1" style={{ color: '#ff8a95' }}>
          <i className="bi bi-exclamation-triangle-fill me-1"></i>
          Does not overlap this event
        </div>
      )}
      {shift.userName && (
        <div>
          <strong>Host:</strong> {shift.userName}
        </div>
      )}
      {shift.summary && (
        <div>
          <strong>Summary:</strong>
          <div style={{ marginLeft: '1rem', marginTop: '0.25rem' }}>{shift.summary}</div>
        </div>
      )}
      {shift.status && (
        <div>
          <strong>Status:</strong> {shift.status}
        </div>
      )}
      {shift.type && (
        <div>
          <strong>Type:</strong> {shift.type}
        </div>
      )}
      {shift.locationName && (
        <div style={{ whiteSpace: 'nowrap' }}>
          <strong>Location:</strong> {shift.locationName}
        </div>
      )}
      {(shift.startTime || shift.endTime) && (
        <div>
          <strong>Time:</strong> {formatTime(shift.startTime, shift.timezone)} - {formatTime(shift.endTime, shift.timezone)}
        </div>
      )}
    </div>
  );
};

/**
 * Render a host badge with optional shift tooltip
 */
export const renderHostWithTooltip = (
  host: string | undefined | null,
  url: string | undefined | null,
  shift?: ShiftTooltipConfig,
  eventId?: string
): React.ReactNode => {
  if (!host) return null;

  const badge = renderHostBadge(host, url, { mismatch: shift?.mismatch });

  if (!badge) return null;

  // If shift data is available, wrap with tooltip
  if (shift && eventId) {
    const tooltipContent = renderShiftTooltipContent(shift);

    // Calculate tooltip width based on location text
    const locationText = shift.locationName ? `Location: ${shift.locationName}` : '';
    const locationWidth = locationText ? measureTextWidth(locationText) : 0;
    const tooltipMaxWidth = locationWidth > 0 ? locationWidth + 50 : 350;

    return renderWithTooltip(badge, tooltipContent, `shift-tooltip-${eventId}`, {
      maxWidth: tooltipMaxWidth,
    });
  }

  return badge;
};

// ============================================================================
// Default Cell Renderer Registry
// ============================================================================

/**
 * Default registry of cell renderers
 * Can be extended or overridden for specific use cases
 */
export const defaultCellRenderers: CellRendererRegistry = {
  text: {
    renderer: renderText,
    tooltip: (value) => value || '',
  },
  code: {
    renderer: renderCode,
    tooltip: (value) => value || '',
  },
  semibold: {
    renderer: renderSemibold,
    tooltip: (value) => value || '',
  },
  link: {
    renderer: ({ text, url, external }: { text: string; url: string; external?: boolean }) =>
      renderLink(text, url, external),
    tooltip: ({ text }: { text: string }) => text || '',
  },
  email: {
    renderer: renderEmailLink,
    tooltip: (value) => value || '',
  },
  phone: {
    renderer: renderPhoneLink,
    tooltip: (value) => value || '',
  },
  price: {
    renderer: renderPrice,
    tooltip: (value) => formatPrice(value),
  },
  date: {
    renderer: renderDate,
    tooltip: (value) => formatDate(value),
  },
  time: {
    renderer: renderTime,
    tooltip: (value) => formatTime(value),
  },
  statusBadge: {
    renderer: renderStatusBadge,
    tooltip: (value) => value || '',
  },
  invoicedBadge: {
    renderer: renderInvoicedBadge,
    tooltip: (value) => value || '',
  },
  activityTypeBadge: {
    renderer: renderActivityTypeBadge,
    tooltip: (value) => value || '',
  },
  deviceBadges: {
    renderer: renderDeviceBadges,
    tooltip: (value) => value || '',
  },
  formatBadge: {
    renderer: renderFormatBadge,
    tooltip: (value) => value || '',
  },
  customerTypeBadge: {
    renderer: renderCustomerTypeBadge,
    tooltip: (value) => value || 'Unknown',
  },
};

/**
 * Get a cell renderer from the registry
 */
export const getCellRenderer = (type: string): CellRendererConfig | undefined => {
  return defaultCellRenderers[type];
};

/**
 * Register a custom cell renderer
 */
export const registerCellRenderer = (
  type: string,
  renderer: CellRenderer,
  tooltip?: (value: any) => string
): void => {
  defaultCellRenderers[type] = { renderer, tooltip };
};
